import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ShieldX } from "lucide-react";
import { api, formatApiErrorDetail } from "@/context/AuthContext";

const inputCls = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#B026FF]/60 placeholder:text-white/30";

export const ProfileSection = ({ t, tAuth, user, setUser }) => {
  const [form, setForm] = useState({ name: user.name, username: user.username });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/users/profile", form);
      setUser(data);
      toast.success(t.saved);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-lg space-y-4" data-testid="profile-section">
      <div>
        <label className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{tAuth.name}</label>
        <input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{tAuth.username}</label>
        <input data-testid="profile-username-input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className={inputCls} />
      </div>
      <button data-testid="save-profile-btn" onClick={save} disabled={busy}
        className="px-8 py-3 rounded-full text-xs font-bold uppercase tracking-widest bg-[#B026FF] shadow-[0_0_20px_rgba(176,38,255,0.45)] disabled:opacity-60">
        {busy ? "..." : t.saveProfile}
      </button>
    </div>
  );
};

export const SecurityLogSection = ({ t }) => {
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    api.get("/avatar/audit-logs").then((res) => setLogs(res.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="security-log-section">
      <p className="text-sm text-[#A0A0B0] mb-6">{t.securitySub}</p>
      <div className="space-y-3">
        {logs.map((log) => {
          const ok = log.status !== "unauthorized_attempt";
          return (
            <div key={log._id} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl px-5 py-4" data-testid="audit-log-row">
              <div className="flex items-center gap-3 min-w-0">
                {ok ? <CheckCircle2 size={18} className="text-[#00FFAA] shrink-0" /> : <ShieldX size={18} className="text-red-400 shrink-0" />}
                <div className="min-w-0">
                  <p className="text-sm font-bold text-white truncate">{t.eventNames[log.event] || log.event}</p>
                  <p className="text-[10px] text-white/35 font-mono truncate">sig: {log.signature?.slice(0, 24)}…</p>
                </div>
              </div>
              <div className="text-right shrink-0 ml-4">
                <span className={`text-[9px] font-bold tracking-widest px-2 py-1 rounded-full ${ok ? "bg-[#00FFAA]/10 text-[#00FFAA]" : "bg-red-500/10 text-red-400"}`}>
                  {ok ? t.verified : t.unauthorized}
                </span>
                <p className="text-[10px] text-white/40 mt-1">{new Date(log.timestamp).toLocaleString()}</p>
              </div>
            </div>
          );
        })}
        {logs.length === 0 && <p className="text-xs text-white/30">—</p>}
      </div>
    </div>
  );
};
